import React, { useState, useEffect, useRef } from "react";
import {
  publishVideoCallRequestStatus,
  publishVideoCallResponse,
  subscribeToDeliveryVideoCallNotifications,
} from "./videoCallHelper";
import {
  requestScreenWakeLock,
  releaseScreenWakeLock,
  vibrateForIncomingCall,
  playRingtone,
  stopRingtone,
  stopVibration,
} from "./screenWakeLock";
import { useAuthSelector } from "../../services/selectors/authSelector";
import AnimatedBottomSheet from "../common/animatedBottomSheet";
import {
  acceptDriverDeliveryVideoCall,
  declineDriverDeliveryVideoCall,
  fetchDriverDeliveryVideoCall,
} from "./deliveryVideoCallApi";

const normalizeCallData = (data) => {
  if (!data) return null;

  return {
    callId: data.call_id ?? data.callId,
    status: data.status,
    patientId: data.patient_id ?? data.patientId ?? data.patient?.id,
    patientFirstName: data.patient?.first_name ?? data.patientFirstName,
    patientLastName: data.patient?.last_name ?? data.patientLastName,
    patientPreferredName:
      data.patient?.preferred_name ??
      data.patient_preferred_name ??
      data.patientPreferredName,
    patientName:
      data.patient_name ??
      data.patientName ??
      [data.patient?.last_name, data.patient?.first_name]
        .filter(Boolean)
        .join(" ")
        .trim(),
    driverId:
      data.driver?.id != null
        ? String(data.driver.id)
        : data.driverId != null
          ? String(data.driverId)
          : undefined,
    driverName: data.driver?.name ?? data.driverName,
    pharmacistId: data.pharmacist?.id ?? data.pharmacistId,
    pharmacistName: data.pharmacist?.name ?? data.pharmacistName,
    vonage_session_id: data.vonage_session_id,
    vonage_token: data.vonage_token,
    application_id: data.application_id,
    // Provider pinned by the backend; MediTalk calls also carry livekit_url.
    video_provider: data.video_provider,
    livekit_url: data.livekit_url,
    requested_at: data.requested_at,
    ring_expires_at: data.ring_expires_at,
    accepted_from: data.accepted_from,
  };
};

const isFlutterNativeShell = () => {
  if (typeof window === "undefined") return false;
  const query = window.location?.search || "";
  try {
    return Boolean(
      window.__FLUTTER_NATIVE_VIDEO_CALLS__ ||
        window.DriverApp ||
        query.includes("flutter_app=1") ||
        window.sessionStorage?.getItem("flutter_app") === "1",
    );
  } catch (error) {
    return Boolean(
      window.__FLUTTER_NATIVE_VIDEO_CALLS__ ||
        window.DriverApp ||
        query.includes("flutter_app=1"),
    );
  }
};

const VideoCallRequestHandler = ({
  getPendingNotificationClick,
  onAccept,
  onDecline,
  isInActiveCall,
}) => {
  const {
    pharmacyId,
    driverID,
    isPharmacistForSelectedPharmacy,
    pharmacistIdForSelectedPharmacy,
  } = useAuthSelector();

  const [incomingCall, setIncomingCall] = useState(null);
  const [showIncomingCallUI, setShowIncomingCallUI] = useState(false);
  const ablyRef = useRef(null);
  const incomingCallRef = useRef(null);
  const hasAcceptedRef = useRef(false);
  const lastNotificationCallIdRef = useRef(null);
  const lastNotificationTimeRef = useRef(0);

  const isPharmacist = isPharmacistForSelectedPharmacy;
  const pharmacistId = pharmacistIdForSelectedPharmacy;
  const shouldSuppressReactIncomingCalls = isFlutterNativeShell();

  useEffect(() => {
    if (!isInActiveCall) {
      hasAcceptedRef.current = false;
    }
  }, [isInActiveCall]);

  useEffect(() => {
    if (shouldSuppressReactIncomingCalls) return;
    if (!isPharmacist || !pharmacyId || !pharmacistId) return;

    const closeIncoming = () => {
      incomingCallRef.current = null;
      setShowIncomingCallUI(false);
      setIncomingCall(null);
      releaseScreenWakeLock();
      stopRingtone();
      stopVibration();
    };

    const openIncoming = async (rawData) => {
      const callId = rawData?.callId ?? rawData?.call_id;
      if (!callId) return;
      if (isFlutterNativeShell()) {
        closeIncoming();
        return;
      }
      if (
        rawData?.driverId != null &&
        String(rawData.driverId) === String(driverID)
      )
        return;
      if (isInActiveCall || hasAcceptedRef.current) return;

      try {
        const details = await fetchDriverDeliveryVideoCall(callId);
        const normalized = normalizeCallData(details);
        if (!normalized?.callId) return;
        if (
          normalized.driverId &&
          String(normalized.driverId) === String(driverID)
        )
          return;
        if (
          normalized.pharmacistId != null &&
          String(normalized.pharmacistId) !== String(pharmacistId)
        )
          return;
        if (normalized.status !== "requesting") {
          if (incomingCallRef.current?.callId === normalized.callId)
            closeIncoming();
          return;
        }
        if (isFlutterNativeShell()) {
          closeIncoming();
          return;
        }

        incomingCallRef.current = normalized;
        setIncomingCall(normalized);
        setShowIncomingCallUI(true);
        requestScreenWakeLock();
        vibrateForIncomingCall();
        playRingtone();

        if (
          typeof document !== "undefined" &&
          document.hidden &&
          navigator.serviceWorker?.controller
        ) {
          const now = Date.now();
          const sameCallWithin15s =
            normalized.callId != null &&
            lastNotificationCallIdRef.current === normalized.callId &&
            now - lastNotificationTimeRef.current < 15000;
          if (!sameCallWithin15s) {
            lastNotificationCallIdRef.current = normalized.callId;
            lastNotificationTimeRef.current = now;
            navigator.serviceWorker.controller.postMessage({
              type: "SHOW_NOTIFICATION",
              payload: {
                title: "Incoming Video Call",
                body: `${normalized.driverName || "Assistant"} is calling`,
                data: {
                  callId: normalized.callId,
                  driverId: normalized.driverId,
                  pharmacistId:
                    normalized.pharmacistId != null
                      ? String(normalized.pharmacistId)
                      : undefined,
                  patientId:
                    normalized.patientId != null
                      ? String(normalized.patientId)
                      : undefined,
                  patientName: normalized.patientName,
                  patientPreferredName: normalized.patientPreferredName,
                  pharmacistName: normalized.pharmacistName,
                },
              },
            });
          }
        }
      } catch (error) {
        console.error("Error fetching delivery video call details:", error);
      }
    };

    const handleIncomingEvent = (payload) => {
      if (!payload) return;

      if (payload.type === "delivery_video_call_request") {
        if (
          payload.pharmacist_id != null &&
          String(payload.pharmacist_id) !== String(pharmacistId)
        )
          return;
        openIncoming({
          callId: payload.call_id,
          driverId: payload.driver_id,
          pharmacistId: payload.pharmacist_id,
        });
        return;
      }

      if (payload.type === "delivery_video_call_status") {
        if (
          payload.pharmacist_id != null &&
          String(payload.pharmacist_id) !== String(pharmacistId)
        )
          return;
        if (incomingCallRef.current?.callId === payload.call_id) {
          if (
            ["accepted", "declined", "cancelled", "expired", "ended"].includes(
              payload.status,
            )
          ) {
            closeIncoming();
          }
        }
      }
    };

    const ably = subscribeToDeliveryVideoCallNotifications({
      pharmacyId,
      onEvent: handleIncomingEvent,
    });
    ablyRef.current = ably;

    const handleFcmVideoCallRequest = (event) => {
      if (
        typeof window !== "undefined" &&
        window.__FLUTTER_NATIVE_VIDEO_CALLS__
      )
        return;
      const data = event.detail?.data;
      if (!data?.callId) return;
      if (data.driverId != null && String(data.driverId) === String(driverID))
        return;
      if (
        data.pharmacistId != null &&
        String(data.pharmacistId) !== String(pharmacistId)
      )
        return;
      openIncoming(data);
    };

    const handleNotificationClick = (event) => {
      const data = event.detail?.data;
      if (!data?.callId) return;
      openIncoming(data);
    };

    const applyPendingClick = (pending) => {
      if (!pending?.data?.callId) return;
      if (incomingCallRef.current?.callId === pending.data.callId) return;
      openIncoming(pending.data);
    };

    window.addEventListener(
      "videoCallNotificationClick",
      handleNotificationClick,
    );
    window.addEventListener(
      "videoCallRequestFromFCM",
      handleFcmVideoCallRequest,
    );

    let clearTimeouts = () => {};
    if (typeof getPendingNotificationClick === "function") {
      applyPendingClick(getPendingNotificationClick());
      const t1 = setTimeout(
        () => applyPendingClick(getPendingNotificationClick()),
        600,
      );
      const t2 = setTimeout(
        () => applyPendingClick(getPendingNotificationClick()),
        2000,
      );
      clearTimeouts = () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }

    return () => {
      clearTimeouts();
      if (ably?.connection?.state === "connected") ably.close();
      window.removeEventListener(
        "videoCallNotificationClick",
        handleNotificationClick,
      );
      window.removeEventListener(
        "videoCallRequestFromFCM",
        handleFcmVideoCallRequest,
      );
    };
  }, [
    pharmacyId,
    pharmacistId,
    isPharmacist,
    driverID,
    isInActiveCall,
    getPendingNotificationClick,
    shouldSuppressReactIncomingCalls,
  ]);

  useEffect(() => {
    if (!shouldSuppressReactIncomingCalls) return;
    incomingCallRef.current = null;
    setShowIncomingCallUI(false);
    setIncomingCall(null);
    releaseScreenWakeLock();
    stopRingtone();
    stopVibration();
  }, [shouldSuppressReactIncomingCalls]);

  useEffect(() => {
    if (!incomingCall) return;
    requestScreenWakeLock();
    return () => {
      releaseScreenWakeLock();
    };
  }, [incomingCall]);

  const handleAccept = async () => {
    if (!incomingCall?.callId) return;
    hasAcceptedRef.current = true;
    stopRingtone();
    try {
      const accepted = normalizeCallData(
        await acceptDriverDeliveryVideoCall(
          incomingCall.callId,
          "delivery-app",
        ),
      );
      await Promise.allSettled([
        publishVideoCallResponse({
          pharmacyId,
          driverId: accepted?.driverId ?? incomingCall.driverId,
          callId: accepted?.callId ?? incomingCall.callId,
          status: "accepted",
          pharmacistId,
          pharmacistName:
            accepted?.pharmacistName ??
            incomingCall?.pharmacistName ??
            "Pharmacist",
        }),
        publishVideoCallRequestStatus({
          pharmacyId,
          pharmacistId,
          callId: accepted?.callId ?? incomingCall.callId,
          status: "accepted",
        }),
      ]);
      setShowIncomingCallUI(false);
      incomingCallRef.current = null;
      releaseScreenWakeLock();
      stopVibration();
      if (typeof onAccept === "function") onAccept(accepted);
      setIncomingCall(null);
    } catch (error) {
      console.error("Error accepting video call:", error);
      hasAcceptedRef.current = false;
    }
  };

  const handleDecline = async () => {
    if (!incomingCall?.callId) return;
    const callToDecline = incomingCall;
    stopRingtone();
    stopVibration();
    setShowIncomingCallUI(false);
    incomingCallRef.current = null;
    setIncomingCall(null);
    releaseScreenWakeLock();
    if (typeof onDecline === "function") onDecline(callToDecline);
    try {
      await declineDriverDeliveryVideoCall(callToDecline.callId);
      await Promise.allSettled([
        publishVideoCallResponse({
          pharmacyId,
          driverId: callToDecline.driverId,
          callId: callToDecline.callId,
          status: "declined",
          pharmacistId,
          pharmacistName: callToDecline.pharmacistName ?? "Pharmacist",
        }),
        publishVideoCallRequestStatus({
          pharmacyId,
          pharmacistId,
          callId: callToDecline.callId,
          status: "declined",
        }),
      ]);
    } catch (error) {
      console.error("Error declining video call:", error);
    }
  };

  const closeSheet = () => {
    setShowIncomingCallUI(false);
    stopRingtone();
    stopVibration();
  };

  const openSheet = () => {
    setShowIncomingCallUI(true);
  };

  if (!isPharmacist || shouldSuppressReactIncomingCalls) return null;

  const patientDisplayName =
    [incomingCall?.patientLastName, incomingCall?.patientFirstName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    incomingCall?.patientName ||
    "";
  const patientReferenceName = incomingCall?.patientPreferredName || "";

  const hasIncomingCallMinimized = incomingCall && !showIncomingCallUI;

  return (
    <>
      {hasIncomingCallMinimized && (
        <button
          type="button"
          onClick={openSheet}
          className="fixed bottom-4 left-4 right-4 z-[190] flex items-center gap-2 text-white py-3 px-4 rounded-xl shadow-lg transition-colors hover:opacity-95"
          style={{ backgroundColor: "rgba(39, 39, 42, 0.95)" }}
        >
          <div className="w-10 h-10 rounded-xl bg-blue-800 flex items-center justify-center flex-shrink-0 shadow-md">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0 flex items-center gap-0.5">
            <div className="min-w-0 flex-1 text-left">
              <p className="font-semibold text-sm truncate text-white">
                {patientDisplayName || "Video call"}
              </p>
              {patientReferenceName && (
                <p className="text-[10px] text-blue-100 truncate">
                  Preferred Name: {patientReferenceName}
                </p>
              )}
              <p className="text-xs text-blue-100 truncate">
                {incomingCall?.driverName} is calling
              </p>
            </div>
            <span className="flex-shrink-0 text-sm font-medium text-blue-200">
              Tap to open
            </span>
          </div>
        </button>
      )}

      <AnimatedBottomSheet
        isOpen={!!(showIncomingCallUI && incomingCall)}
        onClose={closeSheet}
        ariaLabel="Incoming Video Call"
      >
            <div className="bg-white pb-8 pt-2 px-6 relative rounded-t-[28px] overflow-hidden">
              <div className="flex items-center justify-between mb-5">
                <div className="flex-1 flex justify-center pt-1">
                  <div
                    className="w-12 h-1 rounded-full bg-gray-300"
                    aria-hidden
                  />
                </div>
                <button
                  type="button"
                  onClick={closeSheet}
                  className="absolute right-6 top-3 p-1.5 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors -mt-0.5"
                  aria-label="Dismiss"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="flex items-center gap-4 mb-8">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-xl bg-blue-800 flex items-center justify-center shadow-md">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-lg font-bold text-gray-900 truncate">
                    {patientDisplayName}
                  </p>
                  {patientReferenceName && (
                    <p className="text-xs text-gray-500 truncate">
                      Preferred Name: {patientReferenceName}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 truncate">
                    {incomingCall?.driverName} is calling
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleAccept}
                  className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm py-3 px-4 rounded-xl transition-colors shadow-md"
                >
                  <svg
                    className="w-5 h-5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <span>Accept</span>
                </button>
                <button
                  type="button"
                  onClick={handleDecline}
                  className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold text-sm py-3 px-4 rounded-xl transition-colors border border-gray-200"
                >
                  <svg
                    className="w-5 h-5 flex-shrink-0 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.516l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z"
                    />
                  </svg>
                  <span>Decline</span>
                </button>
              </div>
            </div>
      </AnimatedBottomSheet>
    </>
  );
};

export default VideoCallRequestHandler;
